import { useCallback, useSyncExternalStore } from 'react';
import type { CustomClip } from './types';

const STORE: { clipsByTrack: Map<number, CustomClip[]>; nextId: number } = {
  clipsByTrack: new Map(),
  nextId: 9_000_001,
};

const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => STORE.clipsByTrack;

const fakeDelay = (ms = 400) => new Promise<void>((r) => setTimeout(r, ms));

export interface UseCustomClipsApi {
  clipsFor: (trackId: number) => CustomClip[];
  createClip: (
    trackId: number,
    args: { name?: string; startMs: number; endMs: number },
  ) => Promise<CustomClip>;
  updateClipRange: (
    clipId: number,
    args: { startMs: number; endMs: number },
  ) => Promise<CustomClip>;
  renameClip: (clipId: number, name: string) => Promise<CustomClip>;
  deleteClip: (clipId: number) => Promise<void>;
}

export const useCustomClips = (): UseCustomClipsApi => {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const clipsFor = useCallback(
    (trackId: number) => snap.get(trackId) ?? [],
    [snap],
  );

  const findClip = (clipId: number): { trackId: number; clip: CustomClip } | null => {
    for (const [trackId, clips] of STORE.clipsByTrack.entries()) {
      const clip = clips.find((c) => c.clipId === clipId);
      if (clip) return { trackId, clip };
    }
    return null;
  };

  const writeTrack = (trackId: number, clips: CustomClip[]) => {
    // Replace the Map immutably — same reason as `seed` above.
    const next = new Map(STORE.clipsByTrack);
    next.set(trackId, clips);
    STORE.clipsByTrack = next;
    emit();
  };

  const createClip: UseCustomClipsApi['createClip'] = async (trackId, { name, startMs, endMs }) => {
    await fakeDelay();
    const existing = STORE.clipsByTrack.get(trackId) ?? [];
    const clip: CustomClip = {
      clipId: STORE.nextId++,
      trackId,
      name: name?.trim() || `Custom Clip ${existing.length + 1}`,
      startMs,
      endMs,
      createdAt: new Date().toISOString(),
    };
    writeTrack(trackId, [...existing, clip]);
    return clip;
  };

  const updateClipRange: UseCustomClipsApi['updateClipRange'] = async (clipId, { startMs, endMs }) => {
    await fakeDelay();
    const found = findClip(clipId);
    if (!found) throw new Error('Clip not found');
    const next = { ...found.clip, startMs, endMs };
    writeTrack(
      found.trackId,
      (STORE.clipsByTrack.get(found.trackId) ?? []).map((c) =>
        c.clipId === clipId ? next : c,
      ),
    );
    return next;
  };

  const renameClip: UseCustomClipsApi['renameClip'] = async (clipId, name) => {
    await fakeDelay();
    const found = findClip(clipId);
    if (!found) throw new Error('Clip not found');
    const next = { ...found.clip, name: name.trim() };
    writeTrack(
      found.trackId,
      (STORE.clipsByTrack.get(found.trackId) ?? []).map((c) =>
        c.clipId === clipId ? next : c,
      ),
    );
    return next;
  };

  const deleteClip: UseCustomClipsApi['deleteClip'] = async (clipId) => {
    await fakeDelay();
    const found = findClip(clipId);
    if (!found) return;
    writeTrack(
      found.trackId,
      (STORE.clipsByTrack.get(found.trackId) ?? []).filter((c) => c.clipId !== clipId),
    );
  };

  return { clipsFor, createClip, updateClipRange, renameClip, deleteClip };
};
